import { useQuery } from '@tanstack/react-query';
import {
  AlertDetailPeople,
  AlertPeople,
  EmailValidationPeople,
  PeopleDetail,
  PeopleInformationSuggestion,
  PeopleProfileQuickView,
  PeopleProfiles,
  PeopleRelatedCompany,
  PeopleRelatedPeoples,
  PeopleIndustry,
} from 'src/model/people';
import { PeopleService } from 'src/services/api';

const QUERY_KEY_SEARCH_LIST_PEOPLE = 'QUERY_KEY_SEARCH_LIST_PEOPLE';
const QUERY_KEY_SEARCH_LIST_PEOPLE_DETAIL = 'QUERY_KEY_SEARCH_LIST_PEOPLE_DETAIL';
const QUERY_KEY_PEOPLE_DETAIL = 'QUERY_KEY_PEOPLE_DETAIL';
const QUERY_KEY_PEOPLE_RELATED_COMPANY = 'QUERY_KEY_PEOPLE_RELATED_COMPANY';
const QUERY_KEY_PEOPLE_RELATED_PEOPLE = 'QUERY_KEY_PEOPLE_RELATED_PEOPLE';
const QUERY_KEY_SUGGESTION_PEOPLE = 'QUERY_KEY_SUGGESTION_PEOPLE';
const QUERY_KEY_ALERT_PEOPLE = 'QUERY_KEY_ALERT_PEOPLE';
const QUERY_KEY_EMAIL_VALIDATION_PEOPLE = 'QUERY_KEY_EMAIL_VALIDATION_PEOPLE';
const QUERY_KEY_SEARCH_PEOPLE_INDUSTRY = 'QUERY_KEY_SEARCH_PEOPLE_INDUSTRY';

export const useQueryListPeople = (query: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_LIST_PEOPLE, query],
    queryFn: () => PeopleService.getListPeople(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleProfiles(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryQuickViewPeopleProfile = (id: string) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_LIST_PEOPLE_DETAIL, id],
    queryFn: () => PeopleService.getQuickViewPeopleProfile(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleProfileQuickView(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(id),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPeopleDetail = (id: string, enabled: boolean) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_PEOPLE_DETAIL, id],
    queryFn: () => PeopleService.getPeopleDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleDetail(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(enabled),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryPeopleRelatedCompany = ({ id = '', params }: { id?: string; params: Record<string, number> }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_PEOPLE_RELATED_COMPANY, id, params],
    queryFn: () => PeopleService.getPeopleRelatedCompany(id, params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleRelatedCompany(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(id),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};
export const useQueryPeopleRelatedPeople = ({ id = '', params }: { id?: string; params: Record<string, number> }) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_PEOPLE_RELATED_PEOPLE, id, params],
    queryFn: () => PeopleService.getPeopleRelatedPeople(id, params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleRelatedPeoples(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(id),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryPeopleInformationSuggestion = (params: Record<string, string>, countryCode: string[]) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_SUGGESTION_PEOPLE, params],
    queryFn: () => PeopleService.getPeopleInformationSuggestion(params, countryCode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleInformationSuggestion(),
    placeholderData: (previousData) => previousData,
    enabled: Object.values(params).some((param) => param),
  });
  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryListAlertPeople = ({ id, params }: { id: string; params: Record<string, number> }) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_ALERT_PEOPLE, id, params],
    queryFn: () => PeopleService.getAlertPeople(id, params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new AlertPeople(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryAlertDetailPeople = ({ peopleId, alertId }: { peopleId: string; alertId: string }) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_ALERT_PEOPLE, peopleId, alertId],
    queryFn: () => PeopleService.getAlertDetailPeople({ peopleId, alertId }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new AlertDetailPeople(),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(peopleId) && Boolean(alertId),
  });

  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryListEmailValidationPeople = ({
  peopleId,
  params,
}: {
  peopleId: string;
  params: Record<string, number>;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_EMAIL_VALIDATION_PEOPLE, peopleId, params],
    queryFn: () => PeopleService.getListEmailValidationPeople({ peopleId, params }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new EmailValidationPeople(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPeopleIndustry = (countryCode: string[]) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_SEARCH_PEOPLE_INDUSTRY, countryCode],
    queryFn: () => PeopleService.getPeopleIndustry(countryCode),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PeopleIndustry(),
  });
  return {
    data,
    isFetching,
  };
};
